import React, { useState, useEffect } from 'react';
import { usePaystackPayment } from 'react-paystack';
import { Button } from './Button';
import { functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';

interface PaywallProps {
  email: string;
  onSubscribe: (plan: 'monthly' | 'yearly') => void;
  onCancel: () => void;
  isDismissible?: boolean;
  onSignOut?: () => void;
}

export const Paywall: React.FC<PaywallProps> = ({ email, onSubscribe, onCancel, isDismissible = true, onSignOut }) => {
  const [loading, setLoading] = useState(false);
  const [processingText, setProcessingText] = useState('Processing...');
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('yearly');
  
  // Allow user to edit email if it's a placeholder or they want to change it for billing
  const [billingEmail, setBillingEmail] = useState(email && email !== 'user@example.com' ? email : '');

  // Update billing email if prop changes (and user hasn't typed)
  useEffect(() => {
      if (email && email !== 'user@example.com') {
          setBillingEmail(email);
      }
  }, [email]);

  // CONFIGURATION
  const PAYSTACK_PUBLIC_KEY = 'pk_live_460da15f5d2cdecc9cce2f257e8c07484ebd69aa'; 
  
  const PLANS = {
    monthly: {
      amount: 10000,
      label: 'Monthly',
      duration: '30 Days'
    },
    yearly: {
      amount: 50000,
      label: 'Yearly',
      duration: '365 Days'
    }
  };

  const planConfig = PLANS[selectedPlan];
  const amountInKobo = Math.ceil(planConfig.amount * 100);
  const finalEmail = billingEmail && billingEmail.includes('@') ? billingEmail : (email || 'guest@loveonread.com');

  // Paystack Config
  const paystackConfig = {
    email: finalEmail,
    amount: amountInKobo,
    publicKey: PAYSTACK_PUBLIC_KEY,
  };

  // Initialize the hook
  const initializePayment = usePaystackPayment(paystackConfig);

  // Separate async verification logic
  const verifyTransaction = async (reference: string) => {
    setLoading(true);
    setProcessingText('Verifying...');
    
    try {
        console.log("Verifying payment ref:", reference);
        const verifyPayment = httpsCallable(functions, 'verifyPayment');
        
        await verifyPayment({
            reference: reference,
            plan: selectedPlan
        });

        console.log("Payment verification successful");
        onSubscribe(selectedPlan);
        
    } catch (error: any) {
        console.error("Verification failed", error);
        alert(`Payment successful, but activation failed: ${error.message || 'Unknown error'}. Please contact support with reference: ${reference}`);
    } finally {
        setLoading(false);
    }
  };

  const onSuccess = (reference: any) => {
      console.log("Paystack Success Data:", reference);
      // reference object looks like: {message: "Approved", reference: "...", status: "success", trans: "...", transaction: "..."}
      verifyTransaction(reference.reference);
  };

  const onClose = () => {
      console.log("Paystack Dialog Closed");
      setLoading(false);
  };

  const handlePaystackPayment = () => {
    setLoading(true);
    setProcessingText('Initializing...');
    
    // Trigger the payment modal
    try {
        initializePayment({onSuccess, onClose});
    } catch (e: any) {
        console.error("Payment init failed:", e);
        setLoading(false);
        alert("Could not load payment provider. Please check your internet connection.");
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-fade-in-up my-auto">
        <div className="bg-gradient-to-br from-brand-600 to-brand-800 p-6 text-center text-white relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <svg viewBox="0 0 100 100" fill="currentColor">
              <path d="M0 0h100v100H0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-1 relative z-10">Upgrade Plan</h2>
          <p className="text-brand-100 text-sm relative z-10">Unlock your full potential.</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-2 gap-3 mb-6">
            <button
              onClick={() => setSelectedPlan('monthly')}
              className={`p-3 rounded-xl border-2 text-center transition-all ${
                selectedPlan === 'monthly'
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-slate-200 text-slate-500 hover:border-slate-300'
              }`}
            >
              <div className="font-semibold text-lg">₦10,000</div>
              <div className="text-xs opacity-80">Monthly</div>
            </button>

            <button
              onClick={() => setSelectedPlan('yearly')}
              className={`relative p-3 rounded-xl border-2 text-center transition-all ${
                selectedPlan === 'yearly'
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-slate-200 text-slate-500 hover:border-slate-300'
              }`}
            >
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                BEST VALUE
              </div>
              <div className="font-semibold text-lg">₦50,000</div>
              <div className="text-xs opacity-80">Yearly</div>
            </button>
          </div>

          <ul className="space-y-3 mb-6 bg-slate-50 p-4 rounded-xl">
            <li className="flex items-center gap-3 text-slate-700 text-sm">
              <span className="text-green-500 text-lg">✓</span> Unlimited AI Conversations
            </li>
            <li className="flex items-center gap-3 text-slate-700 text-sm">
              <span className="text-green-500 text-lg">✓</span> Advanced Dating Scenarios
            </li>
            <li className="flex items-center gap-3 text-slate-700 text-sm">
              <span className="text-green-500 text-lg">✓</span> 
              Access for {selectedPlan === 'monthly' ? '30 Days' : '365 Days'}
            </li>
            <li className="flex items-center gap-3 text-slate-700 text-sm">
              <span className="text-green-500 text-lg">✓</span> 
              <strong>Syncs with Mobile App</strong>
            </li>
          </ul>

          <div className="space-y-3">
            <div className="mb-2">
                <label className="block text-xs text-slate-500 mb-1 ml-1">Receipt Email</label>
                <input 
                    type="email" 
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                    placeholder="Enter email for receipt"
                    className="w-full px-4 py-2 border border-slate-300 rounded-xl text-sm focus:border-brand-500 outline-none transition-all"
                />
            </div>

            <Button 
              onClick={handlePaystackPayment} 
              fullWidth 
              className="text-lg"
              disabled={loading}
            >
              {loading ? processingText : `Pay ₦${PLANS[selectedPlan].amount.toLocaleString()}`}
            </Button>
            
            {loading && (
                <button 
                    onClick={() => setLoading(false)}
                    className="w-full py-2 text-xs text-red-400 hover:text-red-600 underline"
                >
                    Stuck? Cancel
                </button>
            )}
            
            {isDismissible && !loading && (
              <button 
                onClick={onCancel}
                className="w-full py-3 text-sm text-slate-400 hover:text-slate-600"
              >
                Maybe later
              </button>
            )}
            
            {!isDismissible && !loading && (
                <div className="text-center">
                    <p className="text-xs text-red-500 mt-2 mb-2">Your subscription has expired. Please renew to continue.</p>
                </div>
            )}

            {onSignOut && !loading && (
                <button 
                    onClick={onSignOut}
                    className="w-full py-2 text-sm text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                    Sign Out & Switch Account
                </button>
            )}
          </div>
          
          <p className="text-center text-xs text-slate-400 mt-4">
            Secured by Paystack. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
};